import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Send, Mail, Phone, MapPin, MessageSquare, Building2, Users, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import MarketingHeader from "@/components/layout/MarketingHeader";
import { SEO } from "@/components/seo/SEO";
import { z } from "zod";

// Validation schema
const contactSchema = z.object({
  firstName: z.string().trim().min(1, "First name is required").max(50, "First name must be less than 50 characters"),
  lastName: z.string().trim().min(1, "Last name is required").max(50, "Last name must be less than 50 characters"),
  email: z.string().trim().email("Invalid email address").max(255, "Email must be less than 255 characters"),
  company: z.string().trim().max(100, "Company name must be less than 100 characters").optional(),
  department: z.string().min(1, "Please select a department"),
  employeeCount: z.string().optional(),
  message: z.string().trim().min(10, "Message must be at least 10 characters").max(2000, "Message must be less than 2000 characters"),
});

const Contact = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    company: "",
    department: "",
    employeeCount: "",
    message: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const departments = [
    { value: "sales", label: t("contact.departments.sales", "Sales") },
    { value: "support", label: t("contact.departments.support", "Support") },
    { value: "partnerships", label: t("contact.departments.partnerships", "Partnerships") },
    { value: "press", label: t("contact.departments.press", "Press & Media") },
    { value: "careers", label: t("contact.departments.careers", "Careers") },
    { value: "other", label: t("contact.departments.other", "Other") },
  ];

  const employeeCounts = [
    { value: "1-50", label: "1-50" },
    { value: "51-200", label: "51-200" },
    { value: "201-500", label: "201-500" },
    { value: "501-1000", label: "501-1000" },
    { value: "1000+", label: "1000+" },
  ];

  const contactMethods = [
    {
      icon: Mail,
      title: t("contact.methods.email.title", "Email Us"),
      description: t("contact.methods.email.description", "Get a response within 24 hours"),
      value: "hello@renest.io",
    },
    {
      icon: Phone,
      title: t("contact.methods.phone.title", "Call Us"),
      description: t("contact.methods.phone.description", "Mon-Fri from 9am to 6pm CET"),
      value: "+33 1 23 45 67 89",
    },
    {
      icon: MapPin,
      title: t("contact.methods.office.title", "Visit Us"),
      description: t("contact.methods.office.description", "Our Paris headquarters"),
      value: "123 Rue de la Paix, Paris",
    },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    
    // Validate form data
    const result = contactSchema.safeParse(formData);
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        if (err.path[0]) {
          fieldErrors[err.path[0] as string] = err.message;
        }
      });
      setErrors(fieldErrors);
      return;
    }

    setLoading(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      toast({
        title: t("contact.success.title", "Message sent!"),
        description: t("contact.success.description", "We'll get back to you within 24 hours."),
      });
      
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        company: "",
        department: "",
        employeeCount: "",
        message: "",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: t("contact.error.title", "Error"),
        description: t("contact.error.description", "Something went wrong. Please try again."),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO 
        title={t("contact.seo.title", "Contact Us - Renest")}
        description={t("contact.seo.description", "Get in touch with our team. We're here to help you manage global remote work compliance.")}
      />
      <div className="min-h-screen bg-background">
        <MarketingHeader />

        {/* Hero Section */}
        <section className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <Badge variant="outline" className="mb-4">
              <MessageSquare className="h-3 w-3 mr-1" />
              {t("contact.badge", "Get in Touch")}
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
              {t("contact.title", "Let's talk about your needs")}
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              {t("contact.subtitle", "Whether you have questions about our platform, pricing, or need a custom solution, we're here to help.")}
            </p>
          </div>
        </section>

        {/* Contact Methods */}
        <section className="py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-3 gap-6">
              {contactMethods.map((method, index) => (
                <Card key={index} className="text-center">
                  <CardContent className="pt-6">
                    <div className="h-12 w-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <method.icon className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="font-semibold mb-1">{method.title}</h3>
                    <p className="text-sm text-muted-foreground mb-2">{method.description}</p>
                    <p className="text-sm font-medium text-primary">{method.value}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <Card>
              <CardHeader>
                <CardTitle>{t("contact.form.title", "Send us a message")}</CardTitle>
                <CardDescription>
                  {t("contact.form.description", "Fill out the form below and we'll get back to you as soon as possible.")}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">{t("contact.form.firstName", "First Name")} *</Label>
                      <Input
                        id="firstName"
                        value={formData.firstName}
                        onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                        className={errors.firstName ? "border-destructive" : ""}
                      />
                      {errors.firstName && <p className="text-sm text-destructive">{errors.firstName}</p>}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName">{t("contact.form.lastName", "Last Name")} *</Label>
                      <Input
                        id="lastName"
                        value={formData.lastName}
                        onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                        className={errors.lastName ? "border-destructive" : ""}
                      />
                      {errors.lastName && <p className="text-sm text-destructive">{errors.lastName}</p>}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">{t("contact.form.email", "Email")} *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      className={errors.email ? "border-destructive" : ""}
                    />
                    {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="company">{t("contact.form.company", "Company")}</Label>
                      <Input
                        id="company"
                        value={formData.company}
                        onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="employeeCount">{t("contact.form.employeeCount", "Number of Employees")}</Label>
                      <Select 
                        value={formData.employeeCount} 
                        onValueChange={(value) => setFormData(prev => ({ ...prev, employeeCount: value }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={t("contact.form.selectSize", "Select company size")} />
                        </SelectTrigger>
                        <SelectContent>
                          {employeeCounts.map((size) => (
                            <SelectItem key={size.value} value={size.value}>
                              {size.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="department">{t("contact.form.department", "Department")} *</Label>
                    <Select 
                      value={formData.department} 
                      onValueChange={(value) => setFormData(prev => ({ ...prev, department: value }))}
                    >
                      <SelectTrigger className={errors.department ? "border-destructive" : ""}>
                        <SelectValue placeholder={t("contact.form.selectDepartment", "Select a department")} />
                      </SelectTrigger>
                      <SelectContent>
                        {departments.map((dept) => (
                          <SelectItem key={dept.value} value={dept.value}>
                            {dept.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.department && <p className="text-sm text-destructive">{errors.department}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">{t("contact.form.message", "Message")} *</Label>
                    <Textarea
                      id="message"
                      rows={5}
                      value={formData.message}
                      onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                      placeholder={t("contact.form.messagePlaceholder", "Tell us about your needs...")}
                      className={errors.message ? "border-destructive" : ""}
                    />
                    {errors.message && <p className="text-sm text-destructive">{errors.message}</p>}
                  </div>

                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <span className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full" />
                        {t("contact.form.sending", "Sending...")}
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Send className="h-4 w-4" />
                        {t("contact.form.submit", "Send Message")}
                      </span>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Quick Links */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 border-t">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold mb-2">
                {t("contact.quickLinks.title", "Looking for something specific?")}
              </h2>
            </div>
            <div className="grid sm:grid-cols-3 gap-6">
              <Card className="cursor-pointer hover:border-primary transition-colors" onClick={() => window.location.href = '/pricing'}>
                <CardContent className="pt-6 text-center">
                  <Building2 className="h-8 w-8 text-primary mx-auto mb-3" />
                  <h3 className="font-semibold mb-1">{t("contact.quickLinks.pricing.title", "Pricing Plans")}</h3>
                  <p className="text-sm text-muted-foreground">
                    {t("contact.quickLinks.pricing.description", "View our pricing tiers and features")}
                  </p>
                </CardContent>
              </Card>
              <Card className="cursor-pointer hover:border-primary transition-colors" onClick={() => window.location.href = '/about'}>
                <CardContent className="pt-6 text-center">
                  <Users className="h-8 w-8 text-primary mx-auto mb-3" />
                  <h3 className="font-semibold mb-1">{t("contact.quickLinks.about.title", "About Us")}</h3>
                  <p className="text-sm text-muted-foreground">
                    {t("contact.quickLinks.about.description", "Learn about our mission and team")}
                  </p>
                </CardContent>
              </Card>
              <Card className="cursor-pointer hover:border-primary transition-colors" onClick={() => window.location.href = '/country-guides'}>
                <CardContent className="pt-6 text-center">
                  <HelpCircle className="h-8 w-8 text-primary mx-auto mb-3" />
                  <h3 className="font-semibold mb-1">{t("contact.quickLinks.guides.title", "Country Guides")}</h3>
                  <p className="text-sm text-muted-foreground">
                    {t("contact.quickLinks.guides.description", "Explore destination guides")}
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Contact;
